/**
 * Custom Validator utility
 * Provides validation functionality compatible with validatorjs API
 */

// Static storage for custom validators
const customValidators = {};

class ValidationErrors {
  constructor() {
    this.errors = {};
  }

  add(field, message) {
    if (!this.errors[field]) {
      this.errors[field] = [];
    }
    this.errors[field].push(message);
  }

  all() {
    return this.errors;
  }

  has(field) {
    return !!this.errors[field];
  }

  first(field) {
    return this.errors[field] ? this.errors[field][0] : null;
  }

  get(field) {
    return this.errors[field] || [];
  }
}

class Validator {
  constructor(data, rules, customMessages = {}) {
    this.data = data;
    this.rules = rules;
    this.customMessages = customMessages;
    this.attributeNames = {};
    this.errors = new ValidationErrors();
    this.validated = false;
  }

  static register(name, validatorFunction, errorMessage) {
    customValidators[name] = {
      validate: validatorFunction,
      message: errorMessage
    };
  }

  setAttributeNames(names) {
    this.attributeNames = names;
  }

  getAttributeName(field) {
    return this.attributeNames[field] || field;
  }

  passes() {
    if (!this.validated) {
      this.validate();
    }
    return Object.keys(this.errors.all()).length === 0;
  }

  fails() {
    return !this.passes();
  }

  validate() {
    this.validated = true;
    this.errors = new ValidationErrors();

    for (const field in this.rules) {
      const fieldRules = Array.isArray(this.rules[field]) 
        ? this.rules[field] 
        : this.rules[field].split('|').filter(r => r);
      
      const value = this.data[field];
      
      for (const rule of fieldRules) {
        if (!rule) continue; // Skip empty rules
        
        const result = this.validateRule(field, value, rule);
        if (result !== true) {
          this.errors.add(field, result);
        }
      }
    }
  }

  validateRule(field, value, rule) {
    const attributeName = this.getAttributeName(field);
    
    // Handle regex rules
    if (typeof rule === 'string' && rule.startsWith('regex:')) {
      const regexPattern = rule.substring(6);
      let regex;
      
      // Check if pattern is already a regex (starts with /)
      if (regexPattern.startsWith('/')) {
        const lastSlash = regexPattern.lastIndexOf('/');
        const pattern = regexPattern.substring(1, lastSlash);
        const flags = regexPattern.substring(lastSlash + 1);
        regex = new RegExp(pattern, flags);
      } else {
        regex = new RegExp(regexPattern);
      }
      
      if (value && !regex.test(value)) {
        const customKey = `regex.${field}`;
        return this.customMessages[customKey] || `The ${attributeName} format is invalid.`;
      }
      return true;
    }

    // Handle parametered rules (e.g., "max:256", "between:1,65535")
    const colonIndex = rule.indexOf(':');
    let ruleName = rule;
    let ruleValue = null;
    
    if (colonIndex !== -1) {
      ruleName = rule.substring(0, colonIndex);
      ruleValue = rule.substring(colonIndex + 1);
    }

    switch (ruleName) {
      case 'required':
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
          return `The ${attributeName} field is required.`;
        }
        break;

      case 'required_with':
        // required_with:field1,field2 - requires this field if any of the specified fields is present
        if (ruleValue) {
          const requiredWithFields = ruleValue.split(',').map(f => f.trim());
          const anyFieldPresent = requiredWithFields.some(f => {
            const fieldValue = this.data[f];
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
          });
          
          if (anyFieldPresent && (value === undefined || value === null || value === '')) {
            return `The ${attributeName} field is required when ${requiredWithFields.join(' or ')} is present.`;
          }
        }
        break;

      case 'numeric':
        if (value !== undefined && value !== null && value !== '' && isNaN(Number(value))) {
          return `The ${attributeName} must be a number.`;
        }
        break;

      case 'min':
        if (value !== undefined && value !== null && value !== '') {
          const minValue = parseFloat(ruleValue);
          const numValue = Number(value);
          
          // If the value is a valid number (or numeric string), validate as number
          if (!isNaN(numValue)) {
            if (numValue < minValue) {
              const customKey = `min.${field}`;
              return this.customMessages[customKey] || 
                this.customMessages['min']?.replace(':min', minValue) ||
                `The ${attributeName} must be at least ${minValue}.`;
            }
          } else if (typeof value === 'string' && value.length < minValue) {
            // For non-numeric strings, check length
            const customKey = `min.${field}`;
            return this.customMessages[customKey] || 
              `The ${attributeName} must be at least ${minValue} characters.`;
          }
        }
        break;

      case 'max':
        if (value !== undefined && value !== null && value !== '') {
          const maxValue = parseFloat(ruleValue);
          const numValue = Number(value);
          
          // If the value is a valid number (or numeric string), validate as number
          if (!isNaN(numValue)) {
            if (numValue > maxValue) {
              const customKey = `max.${field}`;
              return this.customMessages[customKey] || 
                this.customMessages['max']?.replace(':max', maxValue) ||
                `The ${attributeName} may not be greater than ${maxValue}.`;
            }
          } else if (typeof value === 'string' && value.length > maxValue) {
            // For non-numeric strings, check length
            const customKey = `max.${field}`;
            return this.customMessages[customKey] || 
              `The ${attributeName} may not be greater than ${maxValue} characters.`;
          }
        }
        break;

      case 'between':
        if (value !== undefined && value !== null && value !== '') {
          const [min, max] = ruleValue.split(',').map(v => parseFloat(v.trim()));
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < min || numValue > max) {
            const customKey = `between.${field}`;
            return this.customMessages[customKey] || 
              `The ${attributeName} must be between ${min} and ${max}.`;
          }
        }
        break;

      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return `The ${attributeName} must be a valid email address.`;
        }
        break;

      case 'url':
        if (value) {
          try {
            new URL(value);
          } catch {
            return `The ${attributeName} must be a valid URL.`;
          }
        }
        break;

      case 'alpha':
        if (value && !/^[a-zA-Z]+$/.test(value)) {
          return `The ${attributeName} may only contain letters.`;
        }
        break;

      case 'alpha_num':
        if (value && !/^[a-zA-Z0-9]+$/.test(value)) {
          return `The ${attributeName} may only contain letters and numbers.`;
        }
        break;

      case 'alpha_dash':
        if (value && !/^[a-zA-Z0-9_-]+$/.test(value)) {
          return `The ${attributeName} may only contain letters, numbers, dashes and underscores.`;
        }
        break;

      case 'in':
        if (value !== undefined && value !== null && value !== '') {
          const allowedValues = ruleValue.split(',').map(v => v.trim());
          if (!allowedValues.includes(String(value))) {
            return `The selected ${attributeName} is invalid.`;
          }
        }
        break;

      case 'not_in':
        if (value !== undefined && value !== null && value !== '') {
          const disallowedValues = ruleValue.split(',').map(v => v.trim());
          if (disallowedValues.includes(String(value))) {
            return `The selected ${attributeName} is invalid.`;
          }
        }
        break;

      case 'boolean':
        if (value !== undefined && value !== null && value !== '' && 
            ![true, false, 0, 1, '0', '1'].includes(value)) {
          return `The ${attributeName} field must be true or false.`;
        }
        break;

      case 'integer':
        if (value !== undefined && value !== null && value !== '') {
          const numValue = Number(value);
          if (isNaN(numValue) || !Number.isInteger(numValue)) {
            const customKey = `integer.${field}`;
            return this.customMessages[customKey] || 
              this.customMessages['integer'] ||
              `The ${attributeName} must be an integer.`;
          }
        }
        break;

      case 'array':
        if (value !== undefined && value !== null && !Array.isArray(value)) {
          return `The ${attributeName} must be an array.`;
        }
        break;

      case 'string':
        if (value !== undefined && value !== null && typeof value !== 'string') {
          return `The ${attributeName} must be a string.`;
        }
        break;

      case 'date':
        if (value !== undefined && value !== null && value !== '') {
          const date = value instanceof Date ? value : new Date(value);
          if (isNaN(date.getTime())) {
            return `The ${attributeName} must be a valid date.`;
          }
        }
        break;

      default:
        // Check for custom validators
        if (customValidators[ruleName]) {
          const customValidator = customValidators[ruleName];
          const isValid = customValidator.validate(value, ruleValue, attributeName);
          if (!isValid) {
            let message = customValidator.message;
            // Replace :attribute placeholder with actual attribute name
            message = message.replace(':attribute', attributeName);
            // Replace :value placeholder if present
            if (ruleValue) {
              message = message.replace(':value', ruleValue);
            }
            return message;
          }
        }
        // Unknown rule - ignore silently
        break;
    }

    return true;
  }
}

export default Validator;
